import { useRef, useState } from "react";
import { Check, Copy, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/frontend/components/ui/button";
import type {
  CopyChipClickEvent,
  CopyChipStatusState,
  CopyChipProps,
  CopyChipState,
  CopyFieldProps,
  CopyFieldRowProps,
} from "@/frontend/types/copy-field";

/**
 * Copy-to-clipboard primitive with two shapes.
 * `variant="field"` (default) — labeled read-only input row; `masked` shows bullets and reveals on hover or focus of the copy button.
 * `variant="chip"` — inline `<code>` badge with a copy icon; used inside tables and dense rows.
 */
export function CopyField(props: CopyFieldProps) {
  if (props.variant === "chip") return <CopyChip value={props.value} />;
  return <CopyFieldRow label={props.label} value={props.value} masked={props.masked ?? false} />;
}

function CopyFieldRow({ label, value, masked }: CopyFieldRowProps) {
  const [copied, setCopied] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const hasValue = value.length > 0;
  const revealed = hovered || focused;
  const displayValue = hasValue ? (masked && !revealed ? "••••••••••••••••••••" : value) : "Unavailable";

  /** Copies the real value and surfaces unavailable or clipboard-denied states. */
  const handleCopy = async () => {
    if (!hasValue) {
      toast.error(`${label} is unavailable`);
      return;
    }

    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Can't copy. Try selecting the value manually.");
    }
  };

  return (
    <div>
      <label className="text-xs font-medium text-foreground block mb-1">{label}</label>
      <div className="relative">
        <input
          readOnly
          value={displayValue}
          className="h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 pr-9 text-sm text-foreground cursor-default outline-none"
        />
        <button
          type="button"
          onClick={handleCopy}
          disabled={!hasValue}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors disabled:cursor-not-allowed disabled:opacity-40"
        >
          {copied ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
        </button>
      </div>
    </div>
  );
}

function CopyChip({ value }: CopyChipProps) {
  const [state, setState] = useState<CopyChipState>("idle");
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showCopyStatus = (next: CopyChipStatusState) => {
    setState(next);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setState("idle"), next === "failed" ? 2000 : 1200);
  };

  /** Copies the value to the clipboard and briefly shows the outcome on the chip. */
  const copy = async (e: CopyChipClickEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(value);
      showCopyStatus("copied");
    } catch {
      showCopyStatus("failed");
    }
  };

  const Icon = state === "copied" ? Check : state === "failed" ? X : Copy;
  const iconClass = state === "copied" ? "size-3 text-emerald-600" : state === "failed" ? "size-3 text-red-600" : "size-3";
  return (
    <div className="flex items-center gap-1.5 text-xs">
      <code className="font-mono bg-muted rounded px-1.5 py-0.5 max-w-full truncate" title={value}>{value}</code>
      <Button
        type="button"
        size="icon"
        variant="ghost"
        className="size-6 shrink-0"
        onClick={copy}
        title={state === "failed" ? "Copy failed, try again" : `Copy ${value}`}
        aria-label={state === "failed" ? "Copy failed" : `Copy ${value}`}
      >
        <Icon className={iconClass} />
      </Button>
    </div>
  );
}
